// Weekly sales meeting page (spec: specs/SPEC-win-more-deals.md, module weekly-sales-page).
// One week Sunday → Saturday (Riyadh), per salesperson: deals moved and new, stuck deals, quotations waiting,
// and the quarter's target against won + weighted pipeline. Managers and the CEO only; read-only.

import { z } from 'zod';
import type { ComplianceStatus } from './compliance';

export interface WeeklyMove {
  dealId: string; name: string; accountName: string | null; amount: number | null;
  kind: 'new' | 'moved' | 'won' | 'lost';
  fromStage: string | null; toStage: string | null; at: string;
}
export interface WeeklyStuck {
  dealId: string; name: string; accountName: string | null; amount: number | null; stageName: string;
  /** e.g. "No contact for 21 days", "No next step", "Close date passed (12 Oct)". */
  reasons: string[];
}
export interface WeeklyQuote {
  dealId: string; name: string; accountName: string | null; amount: number | null;
  submitted: string; daysWaiting: number; validUntil: string | null;
  followUpsDone: number; followUpsDue: number; followUpsOverdue: number; nextFollowUp: string | null;
}
/** An open deal with its health now (red first), and why. */
export interface WeeklyHealthDeal {
  dealId: string; name: string; accountName: string | null; amount: number | null;
  health: 'red' | 'yellow' | 'green'; reasons: string[];
}
/** Open pipeline at the end of the week against the end of the week before, and deals whose health changed. */
export interface WeeklyChange { pipelineNow: number; pipelineBefore: number; better: number; worse: number }
/** An action agreed in the meeting; it is also a task for the person who does it. */
export interface WeeklyAction {
  id: string; text: string; assigneeId: string; assigneeName: string; dueDate: string;
  /** From its task: done, late (due date passed), open. */
  status: 'done' | 'late' | 'open'; doneAt: string | null;
}
export interface WeeklyTarget { quarter: string; target: number | null; won: number; weighted: number }
export interface WeeklyPerson {
  userId: string; name: string;
  /** Managers (people with a role below theirs) have no target line. */
  isManager: boolean;
  moves: WeeklyMove[]; stuck: WeeklyStuck[]; quotes: WeeklyQuote[];
  target: WeeklyTarget | null;
  /** Open deals by health (red, yellow, then green). */
  health: WeeklyHealthDeal[];
  /** Null until the daily history covers both ends of the week. */
  change: WeeklyChange | null;
  /** Agreed in the previous week's meeting, and in this week's. */
  lastActions: WeeklyAction[]; actions: WeeklyAction[];
  /** CRM compliance today, as on the Compliance page (null: not visible to the viewer, or not a scored salesperson). */
  compliance: { score: number | null; status: ComplianceStatus; change: number | null; rulesChangedOn: string | null; methodologyChanged: boolean } | null;
}
export interface WeeklySales {
  from: string; to: string; today: string; quarter: string;
  people: WeeklyPerson[];
  /** People an action can be given to (the salespeople on the page and the viewer). */
  assignees: { id: string; name: string }[];
  team: { created: number; createdValue: number; won: number; wonValue: number; lost: number; lostValue: number; stuck: number; quotesWaiting: number; quotesValue: number;
    target: number | null; quarterWon: number; quarterWeighted: number };
}

export const weeklyActionInputSchema = z.object({
  week: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  personId: z.string().uuid(), assigneeId: z.string().uuid(),
  text: z.string().trim().min(1).max(255), dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
